import React, { FC, useState, useEffect, ChangeEvent } from 'react';
import { Box, Typography, TextField, Grid, Alert, Checkbox, FormControl, FormControlLabel } from '@mui/material';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { getOrderDetailsTemplate, sendOrderTemplate } from 'apollo/templates/order';
import StatusDatePicker from 'components/orders/order-details/status-date-picker.component';
import ProductionFinished from 'components/orders/order-details/production-finished.component';
import InvoiceStateModule from 'components/orders/order-details/invoice-state-module.component';
import PerOrderInvoiceActionModule from 'components/orders/order-details/per-order-invoice-action-module.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { InvoicingState, Order, PerOrderSendInvoiceAction, ProductionValueReport } from 'models/order';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { customerSettings } from 'utils/customerSettings';
import { permissions } from 'utils/permission';
import { CONSTANTS } from 'config/constants';
import { User } from 'models/user';
import { LabClient } from 'models/client-directory';
import ApprovedByAutocomplete from './approved-by-autocomplete/approved-by-autocomplete.component';
import { InvoiceType } from 'models/invoice';
import useBus, { dispatch } from 'use-bus';
import { useKeyPress } from 'utils/use-key-press';
import { ApolloCache, Reference } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import {
    setOrderReadyForDeliveryTemplate,
    createTransportForOrderTemplate,
} from 'apollo/templates/transport/mutation.graphql';
import { hasRestorationAction, restorationActionRowsByActionId } from 'utils/restoration-price-rows';
import { computeProductionValueSteps } from 'components/shared/production-value-report/production-value-report.utils';
import InfoText from 'components/shared/info-text.component';
import { getParentChildLabClientComboTemplate } from 'apollo/templates/client-directory/query.graphql';
import Loader from 'components/shared/loader/loader.component';
import { resolveClinicInvoiceSettings } from 'utils/invoice-settings';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { TransportChannel, TransportProvider, TransportServices } from 'models/transport';
import ProductionValueReportComponent from 'components/shared/production-value-report/production-value-report.component';
import { OrderStatus } from 'models/order';
import { transportDateHelper } from 'utils/transport-date-helper';
import moment from 'moment';
import CreateDeliveryTransportSection from './create-delivery-transport-section.component';

type SentByLabDialogProps = {
    onUpdate?: (order: Order) => void;
    onClose: () => void;
    open: boolean;
    orderId?: string;
    qrCodeTechnicianId?: string;
    isUpdateCache?: boolean;
    onHandleDeliveryNoteCreation?: () => void;
};

const SentByLabDialog: FC<SentByLabDialogProps> = ({
    onUpdate,
    onClose,
    open,
    orderId,
    qrCodeTechnicianId,
    isUpdateCache,
    onHandleDeliveryNoteCreation,
}: SentByLabDialogProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'invoices']);
    const currentOrg = currentOrgVar();
    const currentUser = currentUserVar();
    const [labTechnician, setLabTechnician] = useState<User>({} as User);
    const [isOrderInvoiced, setIsOrderInvoiced] = useState(false);
    const [productionState, setProductionState] = useState('');
    const [invoiceState, setInvoiceState] = useState<InvoicingState>(InvoicingState.NOT_READY);
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [invoiceType, setInvoiceType] = useState('');
    const [showInvoicingInfo, setShowInvoicingInfo] = useState(false);
    // DD-3387: per-order-invoice clinics get their own choice instead of the collective one.
    const [showPerOrderInvoicing, setShowPerOrderInvoicing] = useState(false);
    const [perOrderInvoiceAction, setPerOrderInvoiceAction] = useState<PerOrderSendInvoiceAction>(
        PerOrderSendInvoiceAction.DO_NOTHING,
    );
    const [perOrderActionTouched, setPerOrderActionTouched] = useState(false);
    const [isArchive, setIsArchive] = useState(false);
    const [date, setDate] = useState<Date | null>();
    const [showInvoiceNumber, setShowInvoiceNumber] = useState(false);
    const isExternalTechnician = permissions.isExternalTechnician(currentUser.role);
    const [isLoading, setLoading] = useState(false);
    const [order, setOrder] = useState<Order>();
    const [isAddToDeliveryList, setIsAddToDeliveryList] = useState(false);
    const [showAddToDeliveryList, setShowAddToDeliveryList] = useState(false);
    const [showCreateDeliveryTransport, setShowCreateDeliveryTransport] = useState(false);
    const [isCreatingDeliveryTransport, setIsCreatingDeliveryTransport] = useState(false);
    const [deliveryTransportDate, setDeliveryTransportDate] = useState<Date | null>(null);
    const [deliveryTransportDeadline, setDeliveryTransportDeadline] = useState<string>(
        CONSTANTS.TRANSPORT_DEADLINES[0],
    );
    const [orderTransportProvider, setOrderTransportProvider] = useState({} as TransportProvider);
    const { showBoundary } = useDenthubErrorBoundary();

    const [parentClientSettings, setParentClientSettings] = useState<LabClient>({} as LabClient);
    const [childClientSettings, setChildClientSettings] = useState<LabClient>({} as LabClient);

    const [productionValueReportData, setProductionValueReportData] = useState<{
        orderId: string;
        productionValueReports: ProductionValueReport[];
    }>();
    const [isValidProductionValueReport, setIsValidProductionValueReport] = useState(true);

    const [getParentChildLabClientCombo, { loading: parentChildLabClientComboLoading }] = useLazyQuery(
        getParentChildLabClientComboTemplate,
        {
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'network-only',
            onCompleted: ({ parentChildLabClientCombo }) => {
                const [parentClientSettings, childClientSettings] = parentChildLabClientCombo;
                setOrderTransportProvider(childClientSettings?.transportProvider);
                if (parentClientSettings?.invoiceEnabled) {
                    setParentClientSettings(parentClientSettings);
                }
                if (childClientSettings?.invoiceEnabled) {
                    setChildClientSettings(childClientSettings);
                }
            },
            onError(error) {
                showBoundary(error);
            },
        },
    );

    const [getOrder] = useLazyQuery(getOrderDetailsTemplate, {
        onCompleted({ order }) {
            setOrder(order);
            if (order) {
                getParentChildLabClientCombo({
                    variables: {
                        parentId: order.customer?._id,
                        childId: order.deliveryDetails?.destination?._id,
                    },
                });
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        getOrder({
            variables: {
                orderId: orderId,
            },
        });
    }, [orderId]);

    const setType = () => {
        const { clinicType, childHasFullInvoiceConfig, activeClientSettings } = resolveClinicInvoiceSettings(
            order as Order,
            parentClientSettings,
            childClientSettings,
            currentOrg,
        );

        let type = clinicType;

        if (type) {
            setInvoiceType(type);
        }

        let invoicingVisible = true;
        if (childHasFullInvoiceConfig) {
            if (childClientSettings?.customerId) {
                if (!type) {
                    type = childClientSettings.invoiceSettings?.type as string;
                    setInvoiceType(type);
                }
            } else {
                invoicingVisible = false;
            }
        } else if (!type) {
            type = activeClientSettings.invoiceSettings?.type as string;
            setInvoiceType(type);
        }
        const isCollective = type === InvoiceType.COLLECTIVE_INVOICE || type === InvoiceType.COLLECTIVE_DENTIST;
        // Collective clinics choose an invoicing state; per-order clinics choose whether
        // to create the invoice now (DD-3387). Anything else shows no invoicing section.
        setShowInvoicingInfo(invoicingVisible && !!type && isCollective);
        setShowPerOrderInvoicing(invoicingVisible && type === InvoiceType.SINGLE);
    };

    const orderPrice = order?.deliveryDetails?.price ?? 0;
    const isManualInvoiceNumber = order?.supplier?.invoicing?.configuration?.invoiceNumberType === 'MANUAL';

    // Any invoice counts here, not only INVOICED: a scheduled e-invoice created from the
    // order page before the send must not be offered, and created, a second time.
    const hasPerOrderInvoice = !!order?.invoice;

    // The most likely per-order choice, the same way the collective default is picked:
    // warranty or zero price is never invoiced, finished work is invoiced, else nothing.
    useEffect(() => {
        if (!showPerOrderInvoicing || !order) {
            return;
        }
        if (hasPerOrderInvoice) {
            setPerOrderInvoiceAction(PerOrderSendInvoiceAction.DO_NOTHING);
            return;
        }
        if (perOrderActionTouched) {
            return;
        }
        if (order.isWarrantyOrder || orderPrice <= 0) {
            setPerOrderInvoiceAction(PerOrderSendInvoiceAction.SHOULD_NOT_INVOICE);
        } else if (productionState === 'FINISHED') {
            setPerOrderInvoiceAction(PerOrderSendInvoiceAction.CREATE_INVOICE);
        } else {
            setPerOrderInvoiceAction(PerOrderSendInvoiceAction.DO_NOTHING);
        }
    }, [showPerOrderInvoicing, order, productionState, perOrderActionTouched, hasPerOrderInvoice, orderPrice]);

    useEffect(() => {
        if (showPerOrderInvoicing) {
            setShowInvoiceNumber(
                perOrderInvoiceAction === PerOrderSendInvoiceAction.CREATE_INVOICE && isManualInvoiceNumber,
            );
        }
    }, [showPerOrderInvoicing, perOrderInvoiceAction, isManualInvoiceNumber]);

    const onPerOrderInvoiceSelection = (action: PerOrderSendInvoiceAction) => {
        setPerOrderActionTouched(true);
        setPerOrderInvoiceAction(action);
    };

    useEffect(() => {
        if (order && (parentClientSettings?.customerId || childClientSettings?.customerId)) {
            const orderIsInvoiced = order.invoice && order.invoicingState === InvoicingState.INVOICED;
            if (order.isProductionFinished || orderIsInvoiced) {
                setProductionState('FINISHED');
            }
            setIsOrderInvoiced(orderIsInvoiced);
            setType();
            if (!order.invoice) {
                if (order.isWarrantyOrder && order.deliveryDetails?.price === 0) {
                    // Warranty order with zero value - Do not invoice
                    setInvoiceState(InvoicingState.SHOULD_NOT_INVOICE);
                } else if (
                    order.isProductionFinished &&
                    order.deliveryDetails?.price &&
                    order.deliveryDetails.price > 0 &&
                    invoiceType !== InvoiceType.SINGLE
                ) {
                    setInvoiceState(InvoicingState.READY_FOR_INVOICE);
                } else {
                    setInvoiceState(InvoicingState.NOT_READY);
                }
            }
            if (order.tempInvoiceNumber) {
                setInvoiceNumber(order.tempInvoiceNumber);
            }
            const condition =
                !!invoiceType &&
                invoiceType !== InvoiceType.COLLECTIVE_INVOICE &&
                invoiceType !== InvoiceType.COLLECTIVE_DENTIST &&
                invoiceState === InvoicingState.READY_FOR_INVOICE &&
                order?.supplier?.invoicing?.configuration?.invoiceNumberType === 'MANUAL';
            setShowInvoiceNumber(condition);

            if (orderTransportProvider) {
                setShowAddToDeliveryList(orderTransportProvider?.channel === 'NSHIFT');
                setShowCreateDeliveryTransport(orderTransportProvider?.channel === TransportChannel.DENTHUB_TRANSPORT);
            }
        }
    }, [order, parentClientSettings, childClientSettings]);

    useEffect(() => {
        // QR Code happy path
        if (order) {
            if (qrCodeTechnicianId) {
                onProductionSelection('FINISHED');
                setTimeout(() => {
                    dispatch({ type: 'qr-select-technician', payload: { technicianId: qrCodeTechnicianId } });
                }, 600);
            }
        }
    }, [labTechnician, invoiceState, invoiceType]);

    const [changeStatus] = useMutation(sendOrderTemplate, {
        onCompleted: ({ sendOrder }) => {
            onClose();
            onUpdate?.(sendOrder);
            if (productionState === 'FINISHED' && onHandleDeliveryNoteCreation) {
                onHandleDeliveryNoteCreation();
            }
        },
        update(cache: ApolloCache<Order>, { data: { sendOrder } }) {
            if (isUpdateCache) {
                updateCache(cache, sendOrder);
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [setOrderReadyForDelivery] = useMutation(setOrderReadyForDeliveryTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const [createTransportForOrder] = useMutation(createTransportForOrderTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const createDeliveryTransport = async () => {
        if (!order || order.currentDelivery || !deliveryTransportDate) {
            return;
        }
        const dentistName = order.dentist ? order.dentist.fullName : order.created.by.fullName;
        const dateZeroTime = new Date(
            transportDateHelper.nextWorkingDay(deliveryTransportDate, false).setHours(0, 0, 0, 0),
        );
        await createTransportForOrder({
            variables: {
                clinic: order.deliveryDetails?.destination?._id,
                serviceCode: TransportServices.DELIVERY,
                transportDay: moment(dateZeroTime).format('YYYY-MM-DD'),
                userSelectedDate: moment(deliveryTransportDate).format('YYYY-MM-DD'),
                manualPackageCount: 0,
                orderId: order._id,
                dentistName,
                deadline: deliveryTransportDeadline,
            },
        });
    };

    const handleChangeStatus = async () => {
        setLoading(true);
        if (isAddToDeliveryList) {
            await setOrderReadyForDelivery({ variables: { orderId: order?._id } });
        }
        if (isCreatingDeliveryTransport) {
            await createDeliveryTransport();
        }
        let productionValueReports;
        if (productionValueReportData) {
            const actionRowMap = restorationActionRowsByActionId(order?.restorations);
            let hasMissingPriceSpec = false;
            productionValueReports =
                productionValueReportData.productionValueReports
                    ?.flatMap((productionValueReport) =>
                        productionValueReport.actions.map((action) => {
                            const actionRow = actionRowMap.get(action.actionId.toString());

                            if (!actionRow) {
                                hasMissingPriceSpec = true;
                                return null;
                            }

                            const steps = computeProductionValueSteps(action, actionRow, productionValueReport.steps);

                            return {
                                actionId: action.actionId,
                                steps,
                            };
                        }),
                    )
                    .filter(Boolean) || [];

            if (hasMissingPriceSpec) {
                console.log('Missing price specification for one or more actions');
                setLoading(false);
                return;
            }
        }
        await changeStatus({
            variables: {
                orderId: order?._id,
                approvedBy: {
                    fullName: labTechnician.fullName,
                    id: labTechnician._id || null,
                },
                date: date,
                isProductionFinished: productionState === 'FINISHED',
                invoiceAction: showPerOrderInvoicing ? perOrderInvoiceAction : invoiceState,
                isArchive: isArchive,
                invoiceNumber: invoiceNumber,
                productionValueReports: productionValueReports,
            },
        });
        setLoading(false);
    };

    const updateCache = (cache: ApolloCache<Order>, order: Order) => {
        cache.modify({
            fields: {
                deliverySchedule(orderRefs: Reference | (Reference | undefined)[], { readField }) {
                    const noDeadlineOrdersCount = readField('noDeadlineOrdersCount', orderRefs as Reference) as number;
                    if (noDeadlineOrdersCount) {
                        const count = noDeadlineOrdersCount - 1;
                        return { ...orderRefs, noDeadlineOrdersCount: count };
                    }
                    return orderRefs;
                },
                deliveryScheduleNoDeadlineOrders(
                    existingNoDeadlineOrderRefs: Reference | (Reference | undefined)[],
                    { readField },
                ) {
                    if (existingNoDeadlineOrderRefs instanceof Array) {
                        return existingNoDeadlineOrderRefs.filter((noDeadlineOrderRef: Reference | undefined) => {
                            return order._id !== readField('_id', noDeadlineOrderRef);
                        });
                    }
                    return [];
                },
            },
        });
    };

    const handleDateChange = (date: Date | null) => {
        setDate(date);
    };

    const onProductionSelection = (value: string) => {
        setProductionState(value);
        if (order) {
            setOrder({ ...order, isProductionFinished: value === 'FINISHED' });
        }
    };

    const onInvoiceSelection = (value: string) => {
        setInvoiceState(InvoicingState[value as keyof typeof InvoicingState]);
        const condition =
            !!invoiceType &&
            invoiceType !== InvoiceType.COLLECTIVE_INVOICE &&
            invoiceType !== InvoiceType.COLLECTIVE_DENTIST &&
            value === InvoicingState.READY_FOR_INVOICE &&
            order?.supplier?.invoicing?.configuration?.invoiceNumberType === 'MANUAL';
        setShowInvoiceNumber(condition);
    };

    const onInvoiceNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setInvoiceNumber(value);
    };

    const handleArchive = (event: ChangeEvent<HTMLInputElement>) => {
        setIsArchive(event.target.checked);
    };

    const selectTechnician = (technician: User | any) => {
        setLabTechnician(technician);
    };

    const isFormInvalid = () => {
        return (
            !labTechnician.fullName ||
            !productionState ||
            (productionState === 'FINISHED' && !isValidProductionValueReport) ||
            (showInvoiceNumber && !invoiceNumber) ||
            (isCreatingDeliveryTransport && !deliveryTransportDate)
        );
    };

    const handleAddToDeliveryList = (event: ChangeEvent<HTMLInputElement>) => {
        setIsAddToDeliveryList(event.target.checked);
    };

    useKeyPress(['Enter'], () => {
        if (!isFormInvalid()) {
            handleChangeStatus();
        }
    });
    useBus(
        'qr-ok-button',
        () => {
            if (!isFormInvalid()) {
                handleChangeStatus();
            }
        },
        [order, labTechnician, date, productionState, invoiceState, perOrderInvoiceAction, isArchive, invoiceNumber],
    );
    useBus('qr-cancel-button', () => onClose(), []);

    const hasAtLeastOneAction = hasRestorationAction(order?.restorations);
    const isProductionValueReportEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.PRODUCTION_VALUE_REPORT,
    );
    const handleValidationChange = (valid: boolean) => {
        setIsValidProductionValueReport(valid);
    };

    const handleDataChange = (data: { orderId: string; productionValueReports: ProductionValueReport[] }) => {
        setProductionValueReportData(data);
    };

    if (parentChildLabClientComboLoading) {
        return <Loader />;
    }

    const isTechnicianAdmin = currentUser.role === 'TECHNICIAN_ADMIN';

    return (
        <DHDialog
            fullWidth
            maxWidth="md"
            open={open}
            onClose={onClose}
            title={translate('orders:SEND_THE_ORDER')}
            actions={
                <>
                    <FormControl margin="dense" fullWidth>
                        <FormControlLabel
                            control={<Checkbox color="secondary" checked={isArchive} onChange={handleArchive} />}
                            label={translate('orders:ARCHIVE_THE_ORDER')}
                            data-test="sent-by-lab-dialog.archive-order-checkbox"
                        />
                    </FormControl>
                    <LoadingButton
                        sx={{ minWidth: 250 }}
                        disabled={isFormInvalid()}
                        variant="contained"
                        onClick={handleChangeStatus}
                        loading={isLoading}
                        data-test="sent-by-lab-dialog.change-status-button"
                    >
                        {translate('orders:APPROVE_ORDER')}
                    </LoadingButton>
                </>
            }
        >
            <Box>
                <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                    {translate('orders:LAB_APPROVAL_CONFIRMATION_TEXT')}{' '}
                    <strong>{translate('orders:SENT_BY_LAB')}?</strong>
                </Alert>
            </Box>
            <Box mt={2}>
                <Typography variant="h4" mb={1}>
                    {translate('orders:APPROVED_BY')}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                        <ApprovedByAutocomplete
                            labTechnician={labTechnician}
                            onTechnicianSelected={selectTechnician}
                            dataTest="sent-by-lab-dialog.approved-by-autocomplete"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <StatusDatePicker onChangeDate={handleDateChange} />
                    </Grid>
                </Grid>
                <InfoText text={translate('orders:APPROVED_BY_INSTRUCTIONS')} />
            </Box>

            {!isExternalTechnician && (
                <Box>
                    <Box mb={3}>
                        <ProductionFinished
                            productionState={productionState}
                            onChangeProduction={onProductionSelection}
                            isOrderInvoiced={isOrderInvoiced}
                        />
                    </Box>

                    {isProductionValueReportEnabled &&
                        order &&
                        hasAtLeastOneAction &&
                        productionState === 'FINISHED' && (
                            <Box mb={3}>
                                <Typography variant="h4" gutterBottom>
                                    {translate('orders:PRODUCTION_VALUE_REPORT')}
                                </Typography>

                                <ProductionValueReportComponent
                                    order={order}
                                    isProductionFinished={true}
                                    fromInternalPage={false}
                                    isTechnicianAdmin={isTechnicianAdmin}
                                    onDataChange={handleDataChange}
                                    onValidationChange={handleValidationChange}
                                />
                            </Box>
                        )}
                </Box>
            )}

            {showInvoicingInfo && !isExternalTechnician && order?.invoice?.status !== 'ERROR' ? (
                <Box mt={2}>
                    <Typography variant="h4">{translate('orders:INVOICE_STATE')}</Typography>
                    <InvoiceStateModule
                        invoiceState={invoiceState}
                        onChangeInvoice={onInvoiceSelection}
                        isOrderInvoiced={isOrderInvoiced}
                        order={order}
                    />
                    {isOrderInvoiced ? (
                        <Box>
                            <Alert severity="info" variant="outlined" sx={{ border: 0 }}>
                                {translate('orders:ORDER_HAS_BEEN_INVOICED')}
                            </Alert>
                        </Box>
                    ) : undefined}
                    {!order?.deliveryDetails?.price || order?.deliveryDetails?.price === 0 ? (
                        <Box>
                            <Alert severity="info" variant="outlined" sx={{ border: 0 }}>
                                {translate('orders:ZERO_PRICE_ORDER')}
                            </Alert>
                        </Box>
                    ) : undefined}
                    {showInvoiceNumber ? (
                        <Grid container rowSpacing={3} columnSpacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    value={invoiceNumber}
                                    disabled={isOrderInvoiced}
                                    onChange={onInvoiceNumberChange}
                                    label={translate('invoices:INVOICE_NUMBER')}
                                    data-test="sent-by-lab-dialog.invoice-number-input"
                                />
                            </Grid>
                        </Grid>
                    ) : undefined}
                </Box>
            ) : undefined}

            {showPerOrderInvoicing && !isExternalTechnician && order?.invoice?.status !== 'ERROR' ? (
                <Box mt={2} data-test="sent-by-lab-dialog.per-order-invoicing">
                    <Typography variant="h4">{translate('orders:INVOICE_STATE')}</Typography>
                    <PerOrderInvoiceActionModule
                        action={perOrderInvoiceAction}
                        onChange={onPerOrderInvoiceSelection}
                        disabled={hasPerOrderInvoice}
                        canCreateInvoice={orderPrice > 0}
                    />
                    {hasPerOrderInvoice ? (
                        <Alert severity="info" variant="outlined" sx={{ border: 0 }}>
                            {translate('orders:ORDER_HAS_BEEN_INVOICED')}
                        </Alert>
                    ) : undefined}
                    {orderPrice <= 0 ? (
                        <Alert severity="info" variant="outlined" sx={{ border: 0 }}>
                            {translate('orders:ZERO_PRICE_ORDER')}
                        </Alert>
                    ) : undefined}
                    {showInvoiceNumber ? (
                        <Grid container rowSpacing={3} columnSpacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    required
                                    value={invoiceNumber}
                                    disabled={hasPerOrderInvoice}
                                    onChange={onInvoiceNumberChange}
                                    label={translate('invoices:INVOICE_NUMBER')}
                                    data-test="sent-by-lab-dialog.invoice-number-input"
                                />
                            </Grid>
                        </Grid>
                    ) : undefined}
                </Box>
            ) : undefined}

            {order?.invoice?.status === 'ERROR' ? (
                <Box mt={2}>
                    <Box>
                        <Typography variant="h4">{translate('orders:INVOICE_STATE')}</Typography>
                        <Alert severity="error" variant="outlined" sx={{ border: 0 }}>
                            <strong>{translate('orders:INVOICE_IN_ERROR_STATE')}</strong>
                        </Alert>
                    </Box>
                </Box>
            ) : undefined}

            {showAddToDeliveryList ? (
                <Box mt={2}>
                    <Typography variant="h4">{translate('transport:TRANSPORT')}</Typography>
                    <FormControl margin="dense" fullWidth>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={isAddToDeliveryList || order?.isReadyForDelivery}
                                    onChange={handleAddToDeliveryList}
                                    disabled={order?.isReadyForDelivery}
                                    data-test="sent-by-lab-dialog.add-to-delivery-list-checkbox"
                                />
                            }
                            label={
                                order?.isReadyForDelivery
                                    ? translate('transport:ALREADY_ADDED_TO_DELIVERY_LIST')
                                    : translate('transport:ADD_TO_DELIVERY_LIST')
                            }
                        />
                    </FormControl>
                </Box>
            ) : undefined}

            {showCreateDeliveryTransport ? (
                <CreateDeliveryTransportSection
                    order={order}
                    isCreating={isCreatingDeliveryTransport}
                    onIsCreatingChange={setIsCreatingDeliveryTransport}
                    date={deliveryTransportDate}
                    onDateChange={setDeliveryTransportDate}
                    deadline={deliveryTransportDeadline}
                    onDeadlineChange={setDeliveryTransportDeadline}
                />
            ) : undefined}
        </DHDialog>
    );
};

export default SentByLabDialog;
